import { FC, useContext, useEffect, useMemo, useState } from "react";

import useFetch from "src/hooks/useFetch";
import { APIEndpoints } from "src/types/apiTypes";
import { LoaderContext } from "src/context/loaderContext";

import { DownloadZipResponseType } from "src/pages/dashboard/types";
import { CaseInformationProps } from "./types";
import { downloadPdfFile } from "src/helpers/downloadFile";
import { AuthContext } from "src/context/authContext";
import { ToastContext } from "src/context/toastContext";
import { ToastTypes } from "src/types";
import IndicationModal, { IndicationValue } from "src/components/modals/indicationModal";
import { RequestTreatmentNotTreatingApiResponse } from "src/components/modals/confirmIndication/types";
import { useNavigate } from "react-router-dom";



const CaseInformation: FC<CaseInformationProps> = ({ caseObject }): JSX.Element => {
  const { setLoader, hideLoader } = useContext(LoaderContext);
  const { isManufacturer, defaultView } = useContext(AuthContext);
  const { showToast } = useContext(ToastContext);
  const navigate = useNavigate();
  const [isIndicationModalOpen, setIsIndicationModalOpen] = useState(false);

  const caseId = caseObject?.case_information?.case_id ?? 0;
  const prescriptionId = caseObject?.case_information?.prescription_id ?? 0;
  const patientName = useMemo(() => {
    const firstName = caseObject?.patient_information?.first_name?.trim() || "";
    const lastName = caseObject?.patient_information?.last_name?.trim() || "";
    return `${firstName} ${lastName}`.trim();
  }, [caseObject]);

  const {
    data: downloadCaseData, error: downloadCaseError, mutate: downloadRequestForm, removeErrorAndData: clearRequestFileData
  } = useFetch<DownloadZipResponseType>(APIEndpoints.DOWNLOAD_REQUEST_FORM);

  const {
    data: confirmIndicationData,
    error: confirmIndicationError,
    mutate: updateConfirmIndication
  } = useFetch<RequestTreatmentNotTreatingApiResponse>(APIEndpoints.REQUEST_TREATMENT_NOT_TREATING, {}, { caseId, prescriptionId });

  useEffect(() => {
    if (downloadCaseData || downloadCaseError) {
      if (downloadCaseData) {
        const { response, report_name } = downloadCaseData;
        downloadPdfFile(response, report_name);
      }
      hideLoader();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [downloadCaseData, downloadCaseError]);

  useEffect(() => {
    if (confirmIndicationData || confirmIndicationError) {
      setIsIndicationModalOpen(false);
      if (confirmIndicationData?.code === 200) {
        showToast([`Confirm Indication Scheduled Successfully`], ToastTypes.SUCCESS);
        navigate(defaultView.includes("/") ? defaultView : `/${defaultView}`, { replace: true });
      } else if (confirmIndicationError) {
        showToast([`Unable to confirm indication. Please try again.`], ToastTypes.WARNING);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmIndicationData, confirmIndicationError]);

  const downloadFileByCaseId = () => {
    // OpenForm Enrollment

    if (downloadCaseData || downloadCaseError) clearRequestFileData();
    setLoader();

    if (caseObject && caseObject.case_information)
      downloadRequestForm({}, {}, isManufacturer? { ref_id: caseObject.case_information.case_id, type: 'request', ref_type: 'Case' ,skip_pi:true,benefit_type:caseObject?.case_information?.benefit_investigation?.benefit_type}:{ref_id: caseObject.case_information.case_id, type: 'request', ref_type: 'Case',benefit_type:caseObject?.case_information?.benefit_investigation?.benefit_type});
  };

  const downloadFileByCaseIdAndbenefit = (insurance: string) => {
    if (downloadCaseData || downloadCaseError) clearRequestFileData();
    setLoader();

    if (caseObject && caseObject.case_information)
      downloadRequestForm({}, {}, isManufacturer? { ref_id: insurance, type: 'investigation', ref_type: 'Insurance',skip_pi_investigation: true,benefit_type:caseObject?.case_information?.benefit_investigation?.benefit_type }:{ref_id: insurance, type: 'investigation', ref_type: 'Insurance',benefit_type:caseObject?.case_information?.benefit_investigation?.benefit_type});
  };

  const submitConfirmedIndication = (value: IndicationValue) => {
    if (!value) return;
    updateConfirmIndication({
      prescription: {
        bentype: "confirmed_indication",
        confirmed_indication: value
      }
    });
  };




  return (
    <div className="card rounded-1">
      <div className="card-header bg-success-subtle">
        <h4 className="mb-0 text-black">Case Information</h4>
      </div>
      <div className="card-body">
        <div className="row">

          <div className="col-12 col-md-6">
            <div className="row">
              <div className="col-6 text-end px-3">
                <p>Created:</p>
              </div>
              <div className="col-6">
                <p>{caseObject && caseObject.case_information ? caseObject.case_information.created : ''}</p>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div className="row">
              <div className="col-6 text-end px-3">
                <p>Product:</p>
              </div>
              <div className="col-6">
                <p>
                  {caseObject && caseObject.case_information ? caseObject.case_information.product : ''}
                </p>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div className="row">
              <div className="col-6 text-end px-3">
                <p>Shipment Date:</p>
              </div>
              <div className="col-6">
                <p>
                  {caseObject && caseObject.case_information ? caseObject.case_information.shipment_date : ''}
                </p>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div className="row align-items-center">
              <div className="col-6 text-end px-3">
                <p className="mb-0">Indication:</p>
              </div>
              <div className="col-6">
                {caseObject && caseObject.case_information ? 
                  (() => {
                    if (!isManufacturer && caseObject.case_information.indication_button) {
                      return (<button className="btn btn-sm btn-danger my-1 btn-case_note"
                        type="button"
                        id={`case_note-${caseObject.case_information.case_id}`}
                        onClick={() => setIsIndicationModalOpen(true)}
                        
                      >
                        Flagged for HCP Review
                      </button>)
                    } else if(isManufacturer && caseObject.case_information.indication_button) {
                       return (<button className="btn btn-sm btn-danger my-1 btn-case_note"
                        type="button"
                        id={`case_note-${caseObject.case_information.case_id}`}
                        onClick={() => setIsIndicationModalOpen(true)}
                        disabled
                      >
                        Flagged for HCP Review
                      </button>)
                    }else if (caseObject.case_information.confirmed_indication?.length > 0 && caseObject.case_information.confirmed_indication !== "N/A") {
                      return <p className="mb-0">{caseObject.case_information.confirmed_indication}</p>
                    } else {
                      return <p className="mb-0">{caseObject.case_information.indication || 'N/A'}</p>
                    }
                  })()
                : 'N/A'}
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div className="row">
              <div className="col-6 text-end">
                <p>Benefits Type:</p>
              </div>
              <div className="col-6">
                <p>
                  {caseObject && caseObject.case_information ? caseObject.case_information.benefits_type : ''}
                </p>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div className="row">
              <div className="col-6 text-end px-3">
                <p>CPT Code:</p>
              </div>
              <div className="col-6">
                <p>
                  {caseObject && caseObject.case_information && caseObject.case_information.show_cpt.length > 0 ? caseObject?.case_information?.show_cpt?.length > 0
                    ? caseObject.case_information.show_cpt.join(', ')
                    : '' : ''}
                </p>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div className="row">
              <div className="col-6 text-end px-3">
                <p>Quantity:</p>
              </div>
              <div className="col-6">
                <p>
                  {caseObject && caseObject.case_information ? caseObject.case_information.quantity : ''}
                </p>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div className="row">
              <div className="col-6 text-end px-3">
                <p>Diagnosis Code:</p>
              </div>
              <div className="col-6">
                <p>
                  {caseObject && caseObject.case_information ? caseObject.case_information.diagnosis_code : ''}
                </p>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div className="row">
              <div className="col-6 text-end px-3">
                <p>Note:</p>
              </div>
              <div className="col-6">
                <p>
                  {caseObject && caseObject.case_information ? caseObject.case_information.note : ''}
                </p>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div className="row">
              <div className="col-6 text-end px-3">
                <p>Directions:</p>
              </div>
              <div className="col-6">
                <p>
                  {caseObject && caseObject.case_information ? caseObject.case_information.directions : ''}
                </p>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div className="row">
              <div className="col-6 text-end px-3">
                <p>Written Date:</p>
              </div>
              <div className="col-6">
                <p>{caseObject && caseObject.case_information ? caseObject.case_information.written_date : ''}</p>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div className="row">
              <div className="col-6 text-end px-3">
                <p>Auto X-fer to Pharmacy:</p>
              </div>
              <div className="col-6">
                <p>
                  {caseObject && caseObject.case_information ? caseObject.case_information.auto_xfer : ''}
                </p>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div className="row">
              <div className="col-6 text-end px-3">
                <p className="mb-0">Request Form:</p>
              </div>
              <div className="col-6">
                <p>
                  <i className="bi bi-file-pdf text-danger fw-bold"></i>
                  <button
                    onClick={downloadFileByCaseId}
                    className="btn btn-link link-underline link-underline-opacity-0 link-offset-0-hover link-underline-opacity-75-hover p-0 align-baseline"
                  >
                    Download
                  </button>
                </p>
              </div>
            </div>
          </div>

          <div className="col-12 col-md-6">
            <div className="row">
              <div className="col-6 text-end px-3">
                <p className="mb-0 fw-bold">Benefits Investigations:</p>
              </div>
              <div className="col-6">
                {/* <p>
                  {
                    caseObject && caseObject.case_information && typeof caseObject.case_information.benefit_investigation !== 'string' ?
                      caseObject.case_information.benefit_investigation &&
                        <span key={caseObject.case_information.benefit_investigation.case_id}>
                          {caseObject.case_information.benefit_investigation.case_id}
                        </span>
                    
                      : 'N/A'
                  }
                </p> */}
                <p>
                  <i className="bi bi-file-pdf text-danger fw-bold"></i>
                  <button
                    onClick={() => {
                      downloadFileByCaseIdAndbenefit(caseObject && caseObject.case_information &&
                        caseObject.case_information.benefit_investigation && caseObject.case_information.benefit_investigation.insurance_id)
                    }}
                    className="btn btn-link link-underline link-underline-opacity-0 link-offset-0-hover link-underline-opacity-75-hover p-0 align-baseline"
                  >

                    {caseObject && caseObject.case_information && typeof caseObject.case_information.benefit_investigation !== 'string' &&
                      caseObject.case_information.benefit_investigation &&
                      <span key={caseObject.case_information.benefit_investigation.title}>
                        {caseObject.case_information.benefit_investigation.title}
                      </span>}


                  </button>
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
      <IndicationModal
        caseId={caseId}
        patientName={patientName || "your patient"}
        isOpen={isIndicationModalOpen}
        onClose={() => setIsIndicationModalOpen(false)}
        onSubmit={submitConfirmedIndication}
      />
    </div>
  );
};

export default CaseInformation;
